import { useEffect, useState } from "react";
import BodyCard from "../../../../components/fundamentals/body-card/body-card";
import TableViewHeader from "../../../../components/fundamentals/custom-table-header";
import Table from "../../../../components/fundamentals/table";
import Spinner from "../../../../components/fundamentals/spinner";
import useNotification from "../../../../hooks/use-notification";
import useImperativeDialog from "../../../../hooks/use-imperative-dialog";
import suppliersService from "../../../../services/suppliers.service";
import { Supplier, sourceLabel } from "../../../../models/Supplier";
import SupplierRow from "./SupplierRow";

const VIEWS = ["Fournisseurs"];

/**
 * RAG-29 — Écran « Fournisseurs » : choisir le PIM source de chaque fournisseur.
 * Consomme l'API RAG-19. Recherche par code, filtre par source, changement avec
 * confirmation (qui déclenche une resynchro) et retour visuel.
 */
export default function SuppliersPage() {
  const notify = useNotification();
  const confirm = useImperativeDialog();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [exportingProvider, setExportingProvider] = useState<string | null>(null);

  async function fetchList() {
    setLoading(true);
    setError(false);
    try {
      const result = await suppliersService.list();
      setSuppliers(result.suppliers);
      setSources(result.sources);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchList();
  }, []);

  async function handleChangeSource(provider: string, source: string) {
    const current = suppliers.find((s) => s.provider === provider);
    if (!current || current.source === source) return;

    const confirmed = await confirm({
      heading: "Changer le PIM source",
      text: `Attribuer la source « ${sourceLabel(source)} » au fournisseur ${provider} ? Les produits de ce fournisseur seront remis en file pour être resynchronisés à la prochaine synchronisation.`,
      confirmText: "Oui, changer",
    });
    if (!confirmed) return;

    setBusyProvider(provider);
    try {
      const resynced = await suppliersService.updateSource(provider, source);
      notify(
        "Source mise à jour",
        `${provider} → ${sourceLabel(source)}. ${resynced} produit(s) à resynchroniser.`,
        "success"
      );
      await fetchList();
    } catch (e: any) {
      notify("Modification refusée", e.message, e.status === 403 ? "warning" : "error");
    } finally {
      setBusyProvider(null);
    }
  }

  async function handleExport(provider: string) {
    setExportingProvider(provider);
    try {
      await suppliersService.exportVisuals(provider);
    } catch (e) {
      notify(
        "Export impossible",
        "Impossible d'exporter les produits de ce fournisseur. Réessayez plus tard.",
        "error"
      );
    } finally {
      setExportingProvider(null);
    }
  }

  const filtered = suppliers.filter((s) => {
    const matchSearch = !search || s.provider.toLowerCase().includes(search.toLowerCase());
    const matchSource = sourceFilter === "all" || s.source === sourceFilter;
    return matchSearch && matchSource;
  });

  return (
    <div className="flex flex-col grow h-full">
      <div className="w-full flex flex-col grow">
        <BodyCard customHeader={<TableViewHeader views={VIEWS} />} className="h-fit">
          <div className="flex flex-col gap-y-base">
            <p className="inter-small-regular text-grey-50">
              Choisissez la base PIM de référence de chaque fournisseur. «&nbsp;Automatique&nbsp;»
              conserve le comportement historique&nbsp;: si l'EAN du produit existe dans Interactiv,
              Interactiv prime. Changer une source remet les produits du fournisseur en file de
              resynchronisation.
            </p>

            <div className="flex items-center gap-x-2">
              <span className="inter-small-semibold">Filtrer par source&nbsp;:</span>
              <select
                className="bg-grey-5 border border-gray-20 rounded-rounded h-10 px-small outline-none focus:border-blue-60"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="all">Toutes</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {sourceLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            {error ? (
              <div className="py-large text-center inter-base-regular text-rose-50">
                Impossible de charger les fournisseurs.{" "}
                <button className="underline" onClick={fetchList}>
                  Réessayer
                </button>
              </div>
            ) : (
              <Table
                enableSearch
                searchPlaceholder="Rechercher un code fournisseur"
                searchValue={search}
                handleSearch={(term) => setSearch(term)}
              >
                <Table.Head>
                  <Table.HeadRow>
                    <Table.HeadCell>Code fournisseur</Table.HeadCell>
                    <Table.HeadCell>Produits</Table.HeadCell>
                    <Table.HeadCell>PIM source</Table.HeadCell>
                    <Table.HeadCell>Dernier changement</Table.HeadCell>
                    <Table.HeadCell>Visuels</Table.HeadCell>
                  </Table.HeadRow>
                </Table.Head>
                <Table.Body>
                  {filtered.map((s) => (
                    <SupplierRow
                      key={s.provider}
                      supplier={s}
                      sources={sources}
                      busy={busyProvider === s.provider}
                      exporting={exportingProvider === s.provider}
                      onChangeSource={handleChangeSource}
                      onExport={handleExport}
                    />
                  ))}
                </Table.Body>
              </Table>
            )}

            {loading && (
              <div className="py-large flex justify-center">
                <Spinner />
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div className="py-large text-center text-grey-50 inter-base-regular">
                Aucun fournisseur ne correspond à votre recherche.
              </div>
            )}
          </div>
        </BodyCard>
      </div>
    </div>
  );
}
